import { Container, Currency, Group, Icon, StatusIconSolid, Text } from '@melio/penny';
import { sanitizeId } from '@melio/platform-analytics';
import { PaymentApprovalDecisionStatusEnum, PaymentFullyExpanded } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { FormattedMessage, MessageKey, useMelioIntl } from '@melio/platform-i18n';

import { getPaymentDetailsTitlePostfix } from './PaymentDetailsTop.utils';

export const PaymentDetailsTop = ({ payment }: { payment: PaymentFullyExpanded }) => {
  const { formatMessage, formatCurrency } = useMelioIntl();
  const [isAutoPayEnabled] = useFeature(FeatureFlags.AutoPay, false);

  const titlePostfix = getPaymentDetailsTitlePostfix(payment);
  const isAutoPayment = isAutoPayEnabled && payment.isAutoPayment;
  const isPaymentPendingApproval = payment.approvalDecisionStatus === PaymentApprovalDecisionStatusEnum.Pending;
  const recurringPaymentLastAmount = payment.subscriptionOccurrence?.billSubscription?.lastAmount;

  return (
    <Container>
      <Group variant="vertical" spacing="xxs" justifyContent="space-between" alignItems="flex-start" width="full">
        <Text textStyle="heading2Semi">{formatMessage('widgets.paymentDetails.paymentActivity')}</Text>
        <Text textStyle="body4" color="neutral.darker">
          {formatMessage('widgets.paymentDetails.melioPaymentId', { paymentId: sanitizeId(payment.id) })}
        </Text>
      </Group>
      <Container paddingTop="m">
        <Group width="full" justifyContent="space-between" alignItems="center">
          <Text data-testid="payment-details-top-subtitle" textStyle="body2Semi">
            <FormattedMessage id={`widgets.paymentDetails.${titlePostfix}` as MessageKey} />
          </Text>
          <Group variant="vertical" spacing="xxs" alignItems="flex-end">
            <Group spacing="xs" alignItems="center">
              {payment.subscriptionOccurrence || isAutoPayment ? (
                <Icon type="repeat" data-testid="payment-details-top-repeat-icon" />
              ) : null}
              <Currency data-testid="payment-details-top-currency-amount" amount={payment.amount} />
            </Group>
          </Group>
        </Group>
      </Container>
      {isPaymentPendingApproval && recurringPaymentLastAmount && recurringPaymentLastAmount > payment.amount && (
        <Container paddingTop="xxs">
          <Group
            spacing="xxs"
            justifyContent="flex-end"
            alignItems="center"
            data-testid="payment-details-top-last-amount"
          >
            <StatusIconSolid variant="warning" size="small" />
            <Text textStyle="body4Semi" color="neutral.darker">
              {formatMessage('widgets.paymentDetails.recurring.lastAmount')}{' '}
            </Text>
            <Text textStyle="body4" color="neutral.darker">
              {formatCurrency(recurringPaymentLastAmount)}
            </Text>
          </Group>
        </Container>
      )}
    </Container>
  );
};
